<template>
  <v-dialog
    v-model="dialog"
    max-width="960"
    persistent>

    <v-card class="dialog-card">

      <!-- ── Toolbar ──────────────────────────────────────────────── -->
      <v-toolbar flat class="dialog-toolbar" height="56">
        <div class="toolbar-icon-wrap mr-3">
          <v-icon color="primary" size="20">mdi-file-document-outline</v-icon>
        </div>
        <div class="toolbar-title-block">
          <div class="toolbar-title">Actas del Módulo</div>
          <div v-if="modulo" class="toolbar-subtitle text-truncate">
            <span class="modulo-num-label">{{ toRoman(modulo.numero) }}</span>
            {{ modulo.nombre }}
          </div>
        </div>
        <v-spacer />
        <v-btn
          small
          color="error"
          dark
          depressed
          class="mr-2"
          :loading="descargando"
          :disabled="!pdfUrls[tabKey]"
          @click="descargarPdf">
          <v-icon left small>mdi-file-pdf-box</v-icon>
          Descargar PDF
        </v-btn>
        <v-btn icon small @click="cerrar">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- ── Tabs ─────────────────────────────────────────────────── -->
      <v-tabs
        v-model="tabActiva"
        background-color="white"
        color="primary"
        slider-color="primary">
        <v-tab v-for="tab in tabs" :key="tab.value">
          <v-icon x-small left>{{ tab.icon }}</v-icon>
          {{ tab.label }}
        </v-tab>
      </v-tabs>
      <v-divider />

      <!-- ── Contenido del tab activo ──────────────────────────────── -->
      <div class="dialog-body">

        <!-- Loading -->
        <div v-if="loadingTabs[tabKey]" class="pdf-state">
          <v-progress-circular indeterminate color="primary" size="44" width="3" />
          <p class="state-text mt-3">Cargando acta...</p>
        </div>

        <!-- Error -->
        <div v-else-if="errorTabs[tabKey]" class="pdf-state">
          <v-icon size="48" color="error">mdi-alert-circle-outline</v-icon>
          <p class="state-text mt-2">{{ errorTabs[tabKey] }}</p>
          <v-btn small depressed color="primary" class="mt-2" @click="cargarPdf(tabKey)">
            <v-icon left small>mdi-refresh</v-icon>
            Reintentar
          </v-btn>
        </div>

        <!-- PDF embebido -->
        <iframe
          v-else-if="pdfUrls[tabKey]"
          :src="pdfUrls[tabKey]"
          class="pdf-iframe"
          frameborder="0" />

        <!-- Vacío: esperando carga -->
        <div v-else class="pdf-state">
          <v-progress-circular indeterminate color="grey lighten-2" size="36" width="2" />
          <p class="state-text mt-3">Preparando...</p>
        </div>

      </div>

    </v-card>
  </v-dialog>
</template>

<script>
import ReporteNotaService from '../../services/reporte-nota.service';

const ROMANOS = [
  'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X',
  'XI', 'XII', 'XIII', 'XIV', 'XV', 'XVI', 'XVII', 'XVIII', 'XIX', 'XX',
];

const SERVICIO_MAP = {
  actividad:          (id) => ReporteNotaService.getReportePlanillaNotasAcademico({ programaModuloiId: id }),
  'primera-instancia': (id) => ReporteNotaService.getReportePlanillaNotasPrimeraInstanciaAcademico({ programaModuloiId: id }),
  'segunda-instancia': (id) => ReporteNotaService.getReporteActaNotaSegundaInstanciaAcademico({ programaModuloiId: id }),
  consolidada:        (id) => ReporteNotaService.getReporteActaNotasAcademico({ programaModuloiId: id }),
};

export default {
  name: 'DialogCalificacionModulo',
  data() {
    return {
      dialog: false,
      modulo: null,
      tabActiva: 0,
      tabs: [
        { value: 'actividad',          label: 'Nota Actividad',       icon: 'mdi-pencil-outline' },
        { value: 'primera-instancia',  label: 'Nota 1era Instancia',  icon: 'mdi-numeric-1-box-outline' },
        { value: 'segunda-instancia',  label: 'Nota 2da Instancia',   icon: 'mdi-numeric-2-box-outline' },
        { value: 'consolidada',        label: 'Nota Consolidada',     icon: 'mdi-check-decagram-outline' },
      ],
      pdfUrls: {},
      loadingTabs: {},
      errorTabs: {},
      descargando: false,
    };
  },
  computed: {
    tabKey() {
      return this.tabs[this.tabActiva]?.value || 'actividad';
    },
  },
  watch: {
    tabActiva() {
      this.cargarPdf(this.tabKey);
    },
  },
  methods: {
    abrir(modulo) {
      this.modulo = modulo;
      this.pdfUrls = {};
      this.loadingTabs = {};
      this.errorTabs = {};
      this.descargando = false;
      this.tabActiva = 0;
      this.dialog = true;
      this.$nextTick(() => this.cargarPdf(this.tabKey));
    },
    cerrar() {
      Object.values(this.pdfUrls).forEach((url) => URL.revokeObjectURL(url));
      this.dialog = false;
      this.modulo = null;
      this.pdfUrls = {};
      this.loadingTabs = {};
      this.errorTabs = {};
    },
    async cargarPdf(tipo) {
      if (!this.modulo) return;
      if (this.pdfUrls[tipo] || this.loadingTabs[tipo]) return;

      this.$set(this.loadingTabs, tipo, true);
      this.$set(this.errorTabs, tipo, null);

      try {
        const response = await SERVICIO_MAP[tipo](this.modulo.id);
        const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        this.$set(this.pdfUrls, tipo, url);
      } catch (error) {
        const mensaje = error?.response?.data?.mensaje || error.message || 'Error al obtener el acta';
        this.$set(this.errorTabs, tipo, mensaje);
      } finally {
        this.$set(this.loadingTabs, tipo, false);
      }
    },
    descargarPdf() {
      const tipo = this.tabKey;
      const url = this.pdfUrls[tipo];
      if (!url || !this.modulo) return;

      this.descargando = true;
      const tab = this.tabs.find((t) => t.value === tipo);
      const nombreTab = tab ? tab.label.replace(/\s+/g, '-').toLowerCase() : tipo;
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `acta-${nombreTab}-modulo-${this.modulo.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      this.descargando = false;
    },
    toRoman(num) {
      return ROMANOS[num - 1] || String(num);
    },
  },
};
</script>

<style scoped>
/* ── Dialog card ───────────────────────────────────────────── */
.dialog-card {
  border-radius: 12px !important;
  overflow: hidden;
}

/* ── Toolbar ────────────────────────────────────────────────── */
.dialog-toolbar {
  background: linear-gradient(135deg, #eef3ff 0%, #f8fafc 100%) !important;
  border-bottom: 1px solid #dce6f0;
}

.toolbar-icon-wrap {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.toolbar-title-block {
  overflow: hidden;
}

.toolbar-title {
  font-size: 13px;
  font-weight: 700;
  color: #1a2640;
  line-height: 1.2;
}

.toolbar-subtitle {
  font-size: 11px;
  color: #6b7a8d;
  max-width: 420px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.modulo-num-label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 17px;
  background: #1d4ed8;
  color: #fff;
  border-radius: 3px;
  font-size: 9px;
  font-weight: 700;
  padding: 0 4px;
  flex-shrink: 0;
}

/* ── Área de contenido ──────────────────────────────────────── */
.dialog-body {
  min-height: 68vh;
}

/* ── PDF iframe ─────────────────────────────────────────────── */
.pdf-iframe {
  width: 100%;
  height: 68vh;
  display: block;
  border: none;
}

/* ── Estados (loading / error) ──────────────────────────────── */
.pdf-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 68vh;
  padding: 40px 16px;
  background: linear-gradient(180deg, #f8fafc 0%, #fff 60%);
}

.state-text {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
  text-align: center;
}
</style>
